import React from "react";
import { motion } from "framer-motion";
import { ShieldAlert, ExternalLink, CheckCircle2, Target, Lock } from "lucide-react";

const SEV = {
  low:  { label: "LOW",  ring: "border-emerald-300/30",  pill: "bg-emerald-400/10 text-emerald-200" },
  med:  { label: "MED",  ring: "border-yellow-300/30",   pill: "bg-yellow-400/10 text-yellow-200" },
  high: { label: "HIGH", ring: "border-orange-300/40",   pill: "bg-orange-400/15 text-orange-200" },
  crit: { label: "CRIT", ring: "border-red-400/50",      pill: "bg-red-500/15 text-red-200" },
};

export const BountyCard = ({ bounty, onHunt, onClaim }) => {
  const sev = SEV[bounty.severity] || SEV.med;
  const isSandbox = bounty.track === "sandbox";
  const accepted = bounty.my_status === "accepted";
  const claimed  = bounty.my_status === "claimed";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className={`relative rounded-2xl border ${sev.ring} bg-gradient-to-b from-zinc-950 to-black p-5 overflow-hidden`}
      data-testid={`bounty-card-${bounty.id}`}
    >
      {/* corner ribbon */}
      <div className="absolute top-0 right-0 px-3 py-1 text-[10px] font-display font-black tracking-[0.2em] rounded-bl-xl ${sev.pill}" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-yellow-300/70 font-display">
            {isSandbox ? <ShieldAlert size={12} /> : <ExternalLink size={12} />}
            {isSandbox ? "Sandbox" : `${bounty.platform || "External"}`}
          </div>
          <h3 className="font-display font-black text-lg text-white mt-1 leading-tight">{bounty.title}</h3>
        </div>
        <span className={`px-2 py-1 rounded text-[10px] font-display font-black tracking-wider ${sev.pill}`}>
          {sev.label}
        </span>
      </div>

      <p className="mt-2 text-sm text-white/65 leading-snug line-clamp-3">{bounty.description}</p>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">Bounty</div>
          <div className="font-display font-black text-2xl text-yellow-200">
            ${Number(bounty.payout_usd).toLocaleString()}
          </div>
        </div>
        {accepted ? (
          <div className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/40 text-emerald-200 text-xs font-display font-black flex items-center gap-1.5">
            <CheckCircle2 size={14} /> CLOSED · {bounty.my_score}
          </div>
        ) : claimed ? (
          <div className="px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-400/40 text-cyan-200 text-xs font-display font-black flex items-center gap-1.5">
            <ExternalLink size={14} /> CLAIMED
          </div>
        ) : isSandbox ? (
          <button
            onClick={() => onHunt && onHunt(bounty)}
            data-testid={`bounty-hunt-${bounty.id}`}
            className="px-4 py-2 rounded-lg bg-gradient-to-br from-red-500 to-red-700 text-white font-display font-black text-xs flex items-center gap-1.5 shadow-[0_6px_20px_rgba(239,68,68,0.35)] hover:brightness-110"
          >
            <Target size={14} /> HUNT
          </button>
        ) : (
          <button
            onClick={() => onClaim && onClaim(bounty)}
            data-testid={`bounty-claim-${bounty.id}`}
            className="px-4 py-2 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 text-white font-display font-black text-xs flex items-center gap-1.5 shadow-[0_6px_20px_rgba(34,211,238,0.3)] hover:brightness-110"
          >
            <ExternalLink size={14} /> CLAIM INTEL
          </button>
        )}
      </div>

      {!isSandbox && (
        <div className="mt-3 text-[10px] text-cyan-200/60 italic flex items-center gap-1">
          <Lock size={10} /> External bridge · pursue at your own sovereignty.
        </div>
      )}
    </motion.div>
  );
};

export default BountyCard;
